<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import type { ForecastEvent } from '../internal/types.ts'
import { formatDate } from '@taler/domain'
import { ActionButton } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  events: readonly ForecastEvent[]
  deletingId: string | undefined
}>()
const emit = defineEmits<{ delete: [eventId: string] }>()

const { locale, t } = useI18n()

const rows = computed(() =>
  props.events.map((event) => {
    const loc = locale.value as 'en' | 'hu'
    return {
      event,
      amount: event.amount,
      date: formatDate(event.date, loc),
    }
  }),
)
</script>

<template>
  <div>
    <p v-if="events.length === 0" class="text-[color:var(--color-text-muted)]">
      {{ t('forecast.noEvents') }}
    </p>
    <ul v-else class="flex flex-col divide-y divide-[color:var(--color-border)]">
      <li
        v-for="row in rows"
        :key="row.event.id"
        class="flex items-center justify-between gap-[var(--space-cell)] py-[var(--space-inline)]"
      >
        <div class="flex min-w-0 flex-col gap-[var(--space-hair)]">
          <span class="text-[color:var(--color-text)]">{{ row.event.name }}</span>
          <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
            row.date
          }}</span>
        </div>
        <div class="flex shrink-0 items-center gap-[var(--space-group)]">
          <MoneyAmount :value="row.amount" sign />
          <ActionButton
            type="button"
            variant="quiet"
            :disabled="deletingId === row.event.id"
            :aria-label="`${t('forecast.deleteAction')} ${row.event.name}`"
            @click="emit('delete', row.event.id)"
          >
            {{ deletingId === row.event.id ? t('forecast.deleting') : t('forecast.deleteAction') }}
          </ActionButton>
        </div>
      </li>
    </ul>
  </div>
</template>
